'use client';

import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Text } from '../../../../../components/styled/text';
import { Badge } from '../../../../../components/badge';
import { Card } from '../../../../../components/card';
import { MarkdownPreview } from '../../../../markdown-preview';
import {
  Alert01Icon,
  CheckmarkCircle01Icon,
  ChromeIcon,
} from '@terra/ui-icons';
import type { BrowserOutput } from './types';

type BrowserResultRendererProps = {
  data: BrowserOutput;
  maxHeight?: string;
};

/**
 * Parse browser output data from various formats
 */
export function parseBrowserOutput(data: unknown): BrowserOutput | null {
  if (!data) return null;
  // If it's already an object with result/logs
  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>;

    if (obj.result || obj.logs || obj.error) {
      return {
        result: obj.result as string | undefined,
        logs: obj.logs as BrowserOutput['logs'] | undefined,
        error: obj.error as string | undefined,
      };
    }
  }

  // If it's a string, try to parse as JSON
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);

      return parseBrowserOutput(parsed);
    } catch {
      // Not JSON, treat the string as the result
      return { result: data };
    }
  }

  return null;
}

/**
 * Renderer for AI Browser tool output
 * Shows a clean summary instead of raw JSON
 */
export function BrowserResultRenderer({
  data,
  maxHeight = '400px',
}: BrowserResultRendererProps) {
  const tExecutions = useTranslations('designSystem.executions');
  const tBrowser = useTranslations('designSystem.chat.browserResult');
  const hasAlerts = data.logs?.alerts && data.logs.alerts.length > 0;
  const hasError = !!data.error;

  return (
    <VStack alignItems="start" gap={2} width="100%">
      {/* Header */}
      <HStack gap={1.5} alignItems="center">
        <ChromeIcon size={14} />
        <Text fontSize="xs" fontWeight="medium" color="fg.muted">
          {tBrowser('title')}
        </Text>
        {hasError ? (
          <Badge size="sm" variant="emphasis">
            {tExecutions('error')}
          </Badge>
        ) : (
          <Badge
            size="sm"
            variant="emphasis"
            icon={<CheckmarkCircle01Icon size={10} />}
          >
            {tExecutions('completed')}
          </Badge>
        )}
      </HStack>

      {/* Error message */}
      {data.error && (
        <Card.Root>
          <Card.Body p={2}>
            <Text fontSize="xs" color="fg.error">
              {data.error}
            </Text>
          </Card.Body>
        </Card.Root>
      )}

      {/* Result content */}
      {data.result && (
        <Box
          width="100%"
          maxHeight={maxHeight}
          overflowY="auto"
          bg="bg.subtle"
          p={3}
          borderRadius="sm"
        >
          <MarkdownPreview source={data.result} compact />
        </Box>
      )}

      {/* Alerts */}
      {hasAlerts && (
        <VStack alignItems="start" gap={1} width="100%">
          <HStack gap={1} alignItems="center" color="fg.muted">
            <Alert01Icon size={12} />
            <Text fontSize="xs" fontWeight="medium">
              {tBrowser('browserAlerts', {
                count: data.logs?.alerts?.length ?? 0,
              })}
            </Text>
          </HStack>
          <VStack gap={1} width="100%" pl={4}>
            {data.logs?.alerts?.map((alert, index) => (
              <Text key={index} fontSize="xs" color="fg.muted">
                • {alert}
              </Text>
            ))}
          </VStack>
        </VStack>
      )}
    </VStack>
  );
}
